// ============================================================================
// EXPENSE VOUCHER HOOKS — Phase G
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { expenseService } from '../../api/services/expenseService';
import type { ID } from '../../api/types';
import type {
  Expense,
  ExpenseAllocationView,
  ExpenseListItem,
  ExpenseSearchRequest,
} from '../../api/types/expense.types';

const emptyPagination = {
  currentPage: 1, pageSize: 25, totalPages: 0, totalItems: 0, hasNextPage: false, hasPreviousPage: false,
};

export const useExpenses = (params?: ExpenseSearchRequest) => {
  const [data, setData] = useState<ExpenseListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState(emptyPagination);

  const key = JSON.stringify(params);
  const fetch = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await expenseService.getAll(params);
      setData(res.data); setPagination(res.pagination);
    } catch (err) { setError(err as Error); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => { fetch(); }, [fetch]);
  return { data, loading, error, pagination, refetch: fetch };
};

/** Single voucher with lines + allocations. refreshKey forces a refetch. */
export const useExpense = (id: ID | undefined, refreshKey?: number) => {
  const [data, setData] = useState<Expense | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    if (!id) { setData(null); return; }
    setLoading(true); setError(null);
    try { setData(await expenseService.getById(id)); }
    catch (err) { setError(err as Error); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { fetch(); }, [fetch, refreshKey]);
  return { data, loading, error, refetch: fetch };
};

/** Per-case allocation view (litigation case modal Expenses tab). */
export const useCaseExpenses = (caseId: ID | undefined, refreshKey?: number) => {
  const [data, setData] = useState<ExpenseAllocationView[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    if (!caseId) { setData([]); return; }
    setLoading(true); setError(null);
    try { setData(await expenseService.getByCase(caseId)); }
    catch (err) { setError(err as Error); }
    finally { setLoading(false); }
  }, [caseId]);

  useEffect(() => { fetch(); }, [fetch, refreshKey]);
  return { data, loading, error, refetch: fetch };
};

/** Per-case-group allocation view (case-group modal Expenses tab). */
export const useCaseGroupExpenses = (caseGroupId: ID | undefined, refreshKey?: number) => {
  const [data, setData] = useState<ExpenseAllocationView[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    if (!caseGroupId) { setData([]); return; }
    setLoading(true); setError(null);
    try { setData(await expenseService.getByCaseGroup(caseGroupId)); }
    catch (err) { setError(err as Error); }
    finally { setLoading(false); }
  }, [caseGroupId]);

  useEffect(() => { fetch(); }, [fetch, refreshKey]);
  return { data, loading, error, refetch: fetch };
};
